import { Action, actionCreator, createReducer } from '@trello/redux';
import { raiseAlert } from 'app/gamma/src/modules/state/ui/alerts';
import { MemberResponse } from 'app/gamma/src/types/responses';
import { StandardThunkAction, Dispatch } from 'app/gamma/src/types';
import { getMe } from 'app/gamma/src/selectors/members';
import API from 'app/gamma/src/api';
import { Noun, trackUe, Verb } from '@trello/analytics';
import { State } from 'app/gamma/src/modules/types';
import { emailErrorMapping, EmailErrors } from 'app/src/components/EmailError';
import { Analytics } from '@trello/atlassian-analytics';
import { SourceType } from '@trello/atlassian-analytics/src/constants/Source';

export interface MemberProfileParams {
  fullName: string;
  initials: string;
  username: string;
  bio: string;
}

export enum SaveMessages {
  SAVE = 'save',
  SAVING = 'saving',
  SAVED = 'saved',
}
export const ADD_NEW_EMAIL_REQUEST = Symbol('models/ADD_NEW_EMAIL_REQUEST');
export const ADD_NEW_EMAIL_SUCCESS = Symbol('models/ADD_NEW_EMAIL_SUCCESS');
export const ADD_NEW_EMAIL_ERROR = Symbol('models/ADD_NEW_EMAIL_ERROR');

type AddNewEmailRequestAction = Action<typeof ADD_NEW_EMAIL_REQUEST, null>;
type AddNewEmailSuccessAction = Action<typeof ADD_NEW_EMAIL_SUCCESS, null>;

const addNewEmailRequest = actionCreator<AddNewEmailRequestAction>(
  ADD_NEW_EMAIL_REQUEST,
);
const addNewEmailSuccess = actionCreator<AddNewEmailSuccessAction>(
  ADD_NEW_EMAIL_SUCCESS,
);
const addNewEmailError = actionCreator<AddNewEmailErrorAction>(
  ADD_NEW_EMAIL_ERROR,
);

export type AddNewEmailErrorAction = Action<typeof ADD_NEW_EMAIL_ERROR, string>;

export const UPDATE_MEMBER_PREFS = Symbol('models/UPDATE_MEMBER_PREFS');
export type UpdateMemberPrefsAction = Action<
  typeof UPDATE_MEMBER_PREFS,
  MemberResponse
>;

const SAVE_PROFILE_START = Symbol('ui/SAVE_PROFILE_START');
const SAVE_PROFILE_COMPLETE = Symbol('ui/SAVE_PROFILE_COMPLETE');
const SAVE_PROFILE_RESET = Symbol('ui/SAVE_PROFILE_RESET');
const SAVE_PROFILE_ERROR = Symbol('ui/SAVE_PROFILE_ERROR');

type SaveProfileStartAction = Action<typeof SAVE_PROFILE_START, null>;
type SaveProfileCompleteAction = Action<typeof SAVE_PROFILE_COMPLETE, null>;
type SaveProfileResetAction = Action<typeof SAVE_PROFILE_RESET, null>;
type SaveProfileErrorAction = Action<
  typeof SAVE_PROFILE_ERROR,
  string | SaveProfileErrors[]
>;

const saveProfileStartCreator = actionCreator<SaveProfileStartAction>(
  SAVE_PROFILE_START,
);
const saveProfileCompleteCreator = actionCreator<SaveProfileCompleteAction>(
  SAVE_PROFILE_COMPLETE,
);
const saveProfileReset = actionCreator<SaveProfileResetAction>(
  SAVE_PROFILE_RESET,
);
const saveProfileError = actionCreator<SaveProfileErrorAction>(
  SAVE_PROFILE_ERROR,
);

const RESET_ADD_EMAIL_UI = Symbol('ui/RESET_ADD_EMAIL_UI');
type ResetAddEmailUiAction = Action<typeof RESET_ADD_EMAIL_UI, null>;
export const resetAddEmailUi = actionCreator<ResetAddEmailUiAction>(
  RESET_ADD_EMAIL_UI,
);

export enum SaveProfileErrors {
  None,
  BioTooLong,
  FullNameTooLong,
  FullNameTooShort,
  FullNameHasUrl,
  InitialsWrongLength,
  UsernameInvalid,
  UsernameTaken,
  UsernameTooLong,
  UsernameTooShort,
  Unknown,
}

export const addNewEmail = (
  email: string,
  source?: SourceType,
): StandardThunkAction => {
  return async (dispatch: Dispatch, getState: () => State) => {
    const me = getMe(getState());
    if (!me) {
      return;
    }

    try {
      dispatch(addNewEmailRequest());

      await API.client.rest.post<MemberResponse>('members/me/logins', {
        body: { email },
      });

      dispatch(addNewEmailSuccess());
      if (source) {
        Analytics.sendTrackEvent({
          action: 'created',
          actionSubject: 'accountEmail',
          source,
          attributes: {
            totalAccountEmails: me.logins ? me.logins.length : 0,
          },
        });
      }
    } catch (err) {
      const errorMessage = (err as ApiError).originalMessage;

      dispatch(addNewEmailError(errorMessage));
    }
  };
};

export const changeEmail = (
  email: string,
  loginId: string,
  source?: SourceType,
): StandardThunkAction => {
  return async (dispatch: Dispatch, getState: () => State) => {
    const me = getMe(getState());
    if (!me) {
      return;
    }

    try {
      dispatch(addNewEmailRequest());

      await API.client.rest.put<MemberResponse>(
        `members/me/logins/${loginId}`,
        {
          body: { email },
        },
      );

      dispatch(addNewEmailSuccess());
      if (source) {
        Analytics.sendTrackEvent({
          action: 'updated',
          actionSubject: 'primaryEmail',
          source,
          attributes: {
            totalAccountEmails: me.logins ? me.logins.length : 0,
          },
        });
      }
    } catch (err) {
      const errorMessage = (err as ApiError).originalMessage;

      dispatch(addNewEmailError(errorMessage));
    }
  };
};
const saveProfileErrorMapping: { [key: string]: SaveProfileErrors } = {
  'username is taken': SaveProfileErrors.UsernameTaken,
  'username must be at least 3 characters': SaveProfileErrors.UsernameTooShort,
  'username must be at most 100 characters': SaveProfileErrors.UsernameTooLong,
  'username is invalid: only lowercase letters, underscores, and numbers are allowed':
    SaveProfileErrors.UsernameInvalid,
  'full name must be at least 1 character': SaveProfileErrors.FullNameTooShort,
  'full name must be at most 100 characters': SaveProfileErrors.FullNameTooLong,
  'full name must not contain a url': SaveProfileErrors.FullNameHasUrl,
  'initials must contain 1-4 characters': SaveProfileErrors.InitialsWrongLength,
  'invalid value for bio': SaveProfileErrors.BioTooLong,
};

export const PROFILE_ALERT_AREA = 'PROFILE_ALERT_AREA';
interface MemberHomeProfileSavingState {
  saveMessage: SaveMessages;
  saveErrors?: SaveProfileErrors[];
}

const initialSavingState: MemberHomeProfileSavingState = {
  saveMessage: SaveMessages.SAVE,
};

interface MemberHomeAddEmailState {
  addingEmailRequestActive: boolean;
  addingEmailSuccess: boolean;
  addingEmailError: EmailErrors;
}

const initialAddEmailState: MemberHomeAddEmailState = {
  addingEmailRequestActive: false,
  addingEmailSuccess: false,
  addingEmailError: EmailErrors.None,
};

export interface MemberHomeProfileState
  extends MemberHomeProfileSavingState,
    MemberHomeAddEmailState {}

export const initialMemberHomeProfileState: MemberHomeProfileState = {
  ...initialSavingState,
  ...initialAddEmailState,
};

export const MemberHomeProfileReducer = createReducer(
  initialMemberHomeProfileState,
  {
    [SAVE_PROFILE_START](state) {
      return {
        ...state,
        saveMessage: SaveMessages.SAVING,
      };
    },

    [SAVE_PROFILE_COMPLETE](state) {
      return {
        ...state,
        saveMessage: SaveMessages.SAVED,
        ...('saveErrors' in state ? { saveErrors: undefined } : {}),
      };
    },

    [SAVE_PROFILE_RESET](state) {
      return { ...state, ...initialSavingState };
    },

    [SAVE_PROFILE_ERROR](state, { payload }: SaveProfileErrorAction) {
      const error =
        typeof payload === 'string'
          ? [saveProfileErrorMapping[payload] || SaveProfileErrors.Unknown]
          : payload;
      return {
        ...state,
        saveErrors: error,
      };
    },

    [ADD_NEW_EMAIL_REQUEST](state) {
      return {
        ...state,
        addingEmailRequestActive: true,
        addingEmailSuccess: false,
        addingEmailError: EmailErrors.None,
      };
    },

    [ADD_NEW_EMAIL_SUCCESS](state) {
      return {
        ...state,
        addingEmailRequestActive: false,
        addingEmailSuccess: true,
        addingEmailError: EmailErrors.None,
      };
    },

    [ADD_NEW_EMAIL_ERROR](state, { payload }: AddNewEmailErrorAction) {
      return {
        ...state,
        addingEmailRequestActive: false,
        addingEmailSuccess: false,
        addingEmailError: emailErrorMapping(payload) || EmailErrors.Unknown,
      };
    },

    [RESET_ADD_EMAIL_UI](state) {
      return { ...state, ...initialAddEmailState };
    },
  },
);

export const saveProfileStart = (): StandardThunkAction => async (
  dispatch: Dispatch,
) => {
  dispatch(
    raiseAlert({
      alertArea: PROFILE_ALERT_AREA,
      alertName: SaveMessages.SAVING,
    }),
  );
  dispatch(saveProfileStartCreator());
};

export const saveProfileComplete = (): StandardThunkAction => async (
  dispatch: Dispatch,
) => {
  dispatch(
    raiseAlert({
      alertArea: PROFILE_ALERT_AREA,
      alertName: SaveMessages.SAVED,
    }),
  );
  dispatch(saveProfileCompleteCreator());
};

export const updateMemberProfile = ({
  profile,
  method,
}: {
  profile: MemberProfileParams;
  method?: string;
}): StandardThunkAction => {
  return async (dispatch: Dispatch, getState: () => State) => {
    const me = getMe(getState());
    if (!me) {
      return;
    }

    const invalidInputs = [];

    if (profile.fullName.length < 1) {
      invalidInputs.push(SaveProfileErrors.FullNameTooShort);
    } else if (profile.fullName.length > 100) {
      invalidInputs.push(SaveProfileErrors.FullNameTooLong);
    }

    if (profile.initials.length < 1 || profile.initials.length > 4) {
      invalidInputs.push(SaveProfileErrors.InitialsWrongLength);
    }

    if (profile.username.length < 3) {
      invalidInputs.push(SaveProfileErrors.UsernameTooShort);
    } else if (profile.username.length > 100) {
      invalidInputs.push(SaveProfileErrors.UsernameTooLong);
    }

    if (profile.bio.length > 16384) {
      invalidInputs.push(SaveProfileErrors.BioTooLong);
    }

    if (invalidInputs.length) {
      dispatch(saveProfileReset());
      dispatch(saveProfileError(invalidInputs));
      return;
    }

    dispatch(saveProfileStart());

    try {
      const updatedMember = await API.client.rest.put<MemberResponse>(
        'members/me',
        {
          body: profile,
        },
      );

      dispatch(actionCreator(UPDATE_MEMBER_PREFS)(updatedMember));
      dispatch(saveProfileComplete());

      trackUe({
        category: Noun.MEMBER,
        verb: Verb.UPDATES,
        directObj: Noun.PROFILE,
        method,
      });
    } catch (err) {
      let errorMessage = (err as ApiError).originalMessage;

      // This will be removed in a future PR, originalMessage is a stringified error object
      if (errorMessage.startsWith('{')) {
        try {
          const jsonError = JSON.parse(errorMessage);
          errorMessage = jsonError.message;
        } catch (e) {
          // We don't really care it just means it's not valid JSON
        }
      }

      errorMessage = errorMessage.trim().toLowerCase();

      dispatch(saveProfileReset());
      dispatch(saveProfileError(errorMessage));
    }
  };
};
